import { PageTitle } from 'components/PageTitle'
import { FullPageSpinner } from 'components/Spinner'
import { signInWithCustomToken } from 'firebase/auth'
import { useRootStore } from 'hooks/rootStore'
import { useEffect, useMemo, useState } from 'react'
import { useRepository } from '../../hooks/auth'
import { captureException } from '@sentry/react'
import { useTranslationTyped } from 'i18n/i18n'
import { useLTIMode } from 'hooks/use-lti-mode'
import { OpenOutsideIframeButton } from 'components/OpenOutsideIframeButton'
import { InstitutionIntegrationType } from '@breakoutlearning/firebase-repository/types'
import { areWeInIframe } from 'util/iframe'

function TokenLogin() {
  // const [loggingIn, setLoggingIn] = useState(false)
  const rootStore = useRootStore()
  const repository = useRepository()
  const { tt } = useTranslationTyped()
  const [hasProcessedToken, setHasProcessedToken] = useState(false)

  const queryParams = new URLSearchParams(window.location.search)
  const token = queryParams.get('token')
  const deepLinkJwt = queryParams.get('deepLinkJwt')
  const redirectTo = queryParams.get('redirectTo')
  const landingPage = queryParams.get('landingPage')
  const ltiMode = queryParams.get('ltiMode')
  const impersonateUserId = queryParams.get('impersonateUserId')
  const sectionId = queryParams.get('sectionId')
  const organizationId = queryParams.get('organizationId')
  const placement = queryParams.get('placement')
  const { setLTIMode } = useLTIMode()

  const validatedLTIMode: InstitutionIntegrationType | null = useMemo(() => {
    if (
      ltiMode &&
      Object.values(InstitutionIntegrationType).includes(
        ltiMode as InstitutionIntegrationType
      )
    )
      return ltiMode as InstitutionIntegrationType
    return null
  }, [ltiMode])

  const ltiOptions: ReturnType<typeof useLTIMode>['ltiOptions'] =
    useMemo(() => {
      if (!validatedLTIMode) return null

      const options: typeof ltiOptions = {
        ltiMode: validatedLTIMode,
        placement: '',
      }

      if (sectionId) options.sectionId = sectionId
      if (organizationId) options.organizationId = organizationId
      if (placement) options.placement = placement

      return options
    }, [validatedLTIMode, sectionId, organizationId, placement])

  useEffect(() => {
    if (hasProcessedToken) return

    if (!token) {
      console.error('bad token')
      captureException(new Error('BadTokenError'))
      return
    }

    setHasProcessedToken(true)

    if (deepLinkJwt) {
      localStorage.setItem('deepLinkJwt', deepLinkJwt)
    }

    if (ltiOptions) setLTIMode(ltiOptions.ltiMode, ltiOptions)

    signInWithCustomToken(repository.auth, token)
      .then(async (credential) => {
        // Wait for the user the token signed in. If someone else was already
        // signed in on this browser, the repository still holds their user
        // for a moment, and checking that one would refuse a valid launch
        // and name the wrong person in the support email.
        const breakoutUser = await repository.waitForBreakoutUser(
          credential.user.uid
        )

        if (!breakoutUser.isLtiUser) {
          // refusing to log in without a lti user id
          // this is a security measure to prevent users from logging in
          // as direct breakout users. This is an expected condition, not an
          // error worth reporting to Sentry, so navigate away without
          // capturing an exception.
          if (validatedLTIMode) {
            // Sign-in succeeded, so this is the launching user's account.
            rootStore.navigateToLtiErrorPage(
              tt.lti.login_error(),
              breakoutUser.emailAddress
            )
          }
          return
        }

        if (
          (breakoutUser.isFaculty || breakoutUser.isCorre) &&
          impersonateUserId
        ) {
          rootStore.impersonateUser({
            userId: impersonateUserId,
            hideBanner: true,
          })
        }

        if (landingPage) {
          rootStore.navigateToUrl(landingPage)
          return
        }

        rootStore.navigateTo(
          'instructorLibrary',
          undefined,
          redirectTo
            ? {
                redirectTo: redirectTo,
              }
            : undefined
        )
      })
      .catch((error) => {
        captureException(error)
        if (validatedLTIMode) {
          rootStore.navigateToLtiErrorPage(tt.lti.login_error())
        }
      })
  }, [
    deepLinkJwt,
    token,
    rootStore,
    repository.auth,
    redirectTo,
    landingPage,
    sectionId,
    setLTIMode,
    impersonateUserId,
    tt,
    repository,
    ltiOptions,
    validatedLTIMode,
    hasProcessedToken,
  ])

  return <FullPageSpinner reason="login_with_token_loading" />
}

function IframeProtector({ children }: { children: React.ReactNode }) {
  const queryParams = new URLSearchParams(window.location.search)
  const ejectFromIframe = queryParams.get('ejectFromIframe') === 'true'
  const { tt } = useTranslationTyped()
  const startTime = useMemo(() => Date.now(), [])
  const [isOutdated, setIsOutdated] = useState(false)

  useEffect(() => {
    const outdatedTime = 1000 * 60 * 15 // 15 minutes
    setInterval(() => {
      if (Date.now() - outdatedTime > startTime) {
        setIsOutdated(true)
      }
    }, 1000)
  }, [startTime])

  if (ejectFromIframe && areWeInIframe()) {
    return (
      <div
        data-testid="login-token-eject"
        className="lti-configuration min-h-screen w-screen md:-m-8"
      >
        <div className="">
          <img
            src="/assets/images/logo.png"
            width={64}
            className="absolute top-8 right-8"
            alt={tt.breakout_learning()}
          />
        </div>
        <div className="lti-rocket flex min-h-screen w-full flex-col items-start justify-center">
          <div className="ml-[5vw] max-w-[400px]">
            <div className="mb-5">
              <h2 className="text-headline-small">
                {tt.lti.configuration.eject_headline_small()}
              </h2>
              <h1 className="text-headline-large">
                {tt.lti.configuration.eject_headline_large()}
              </h1>
              <div className="text-body-large">
                {tt.lti.configuration.eject_headline_description()}
              </div>
            </div>
            <div>
              <div>
                {isOutdated && (
                  <div className="flex h-fit min-h-10 w-fit min-w-38 items-center justify-center rounded-xl bg-core-primary px-5 py-1 text-xs leading-[normal] font-semibold text-core-on-primary select-none">
                    {tt.login.reload_application()}
                  </div>
                )}
                {!isOutdated && (
                  <OpenOutsideIframeButton
                    testId="login-token-open-new-tab"
                    className="flex items-center justify-center"
                    href={window.location.href.replace(
                      'ejectFromIframe=true',
                      ''
                    )}
                  >
                    {tt.login.open_application_in_new_tab()}
                  </OpenOutsideIframeButton>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  return <>{children}</>
}
export function LoginWithToken() {
  const { tt } = useTranslationTyped()
  return (
    <IframeProtector>
      <div className="relative box-border flex h-full w-full items-center justify-center overflow-auto rounded-3xl bg-core-tertiary">
        <PageTitle title={tt.login.page_title()} />
        <TokenLogin />
      </div>
    </IframeProtector>
  )
}
